/**
 * Walks a dot-path on `obj`, creating intermediate objects/arrays as needed,
 * and returns the parent of the leaf along with the leaf key. Treats a numeric
 * next-segment as an array index (e.g. 'resume.educations.0.studyType').
 */
function walkToParent(obj: any, path: string): { parent: any, lastKey: string } {
  const keys = path.split(".")
  const lastKey = keys.pop()!

  let current = obj
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]
    const nextKey = keys[i + 1]

    if (!current[key]) {
      const isNextKeyNumeric = nextKey !== undefined && /^\d+$/.test(nextKey)
      current[key] = isNextKeyNumeric ? [] : {}
    }
    current = current[key]
  }

  return { parent: current, lastKey }
}

/**
 * Sets a value in a nested object using dot notation path
 * Supports array indices (e.g., 'resume.educations.0.studyType')
 * @example setNestedValue(obj, 'user.firstName', 'John')
 * @example setNestedValue(obj, 'resume.educations.0.studyType', 'Bachelor')
 */
function setNestedValue(obj: any, path: string, value: any, fieldName?: string): void {
  const { parent, lastKey } = walkToParent(obj, path)

  if (lastKey === "customFields" && fieldName) {
    if (!Array.isArray(parent[lastKey]))
      parent[lastKey] = []

    parent[lastKey].push(
      Array.isArray(value)
        ? { name: fieldName, values: value }
        : { name: fieldName, value },
    )
  }
  else {
    parent[lastKey] = value
  }
}

/**
 * Converts a File object to base64 string
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const base64 = reader.result as string
      // Remove data URL prefix (e.g., "data:application/pdf;base64,")
      const base64Data = base64.split(",")[1]
      resolve(base64Data)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

/**
 * Normalizes a date value to ISO format (YYYY-MM-DD)
 */
function normalizeDate(value: any): string | null {
  if (!value)
    return null

  // If it's already a string in YYYY-MM-DD format, return as-is
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value))
    return value

  // Try to parse and format
  const date = new Date(value)
  if (Number.isNaN(date.getTime()))
    return null

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

/**
 * Transforms file uploads to ApplicationFile format
 */
async function transformFileUpload(
  files: File | File[],
  field: FormField,
): Promise<ApplicationFile[]> {
  if (!files)
    return []

  const fileArray = Array.isArray(files) ? files : [files]

  if (fileArray.length === 0)
    return []

  const category = (field as UploadField).category
  const specification = field.label

  return Promise.all(
    fileArray.map(async file => ({
      filename: file.name,
      filedata: await fileToBase64(file),
      mimeType: file.type,
      ...(category && { category }),
      ...(specification && { specification }),
    })),
  )
}

/**
 * Applies transformation based on field type
 */
async function transformByFieldType(value: any, field: FormField): Promise<any> {
  if (field.type === "upload")
    return transformFileUpload(value, field)

  if (field.type === "datepicker")
    return normalizeDate(value)

  return value
}

/**
 * Appends values into a nested array at the given dot-path. Used for upload
 * fields so multiple fields sharing a path (e.g. `other.files`) accumulate
 * instead of overwriting one another.
 */
function appendToNestedArray(obj: any, path: string, values: any[]): void {
  const { parent, lastKey } = walkToParent(obj, path)
  if (!Array.isArray(parent[lastKey]))
    parent[lastKey] = []
  parent[lastKey].push(...values)
}

// Positional index mirrors buildApplicationRequest's push order, so the
// same skip rules must be applied here. Returns null for non-customFields
// paths, or when the index lies beyond the form-supplied fields.
export function translateCustomFieldErrorPath(
  errorPath: string,
  formConfig: FormConfig,
  formValues: Record<string, any>,
): string | null {
  const match = errorPath.match(/^(.+?\.customFields)\[(\d+)\]\.value$/)
  if (!match)
    return null

  const parentPath = match[1]
  const targetIndex = Number.parseInt(match[2], 10)

  let currentIndex = 0
  for (const field of formConfig.form.fields) {
    if (field.path !== parentPath)
      continue

    const value = formValues[field.name]
    if (value === undefined || value === null)
      continue
    if (!field.required && (value === "" || (Array.isArray(value) && value.length === 0)))
      continue

    if (currentIndex === targetIndex)
      return field.name
    currentIndex++
  }
  return null
}

/**
 * Builds an ApplicationRequest from form data and form config
 */
export async function buildApplicationRequest(
  formData: Record<string, any>,
  formConfig: FormConfig,
  staticData: {
    jobID: string
    apiConfigurationName: string | null
    applicationType: ApplicationType
    externalReferenceId?: string | null
  },
  customFields?: CustomField[],
): Promise<ApplicationRequest> {
  const request: Partial<ApplicationRequest> = {
    jobID: staticData.jobID,
    apiConfigurationName: staticData.apiConfigurationName,
    applicationType: staticData.applicationType,
    ...(staticData.externalReferenceId && { externalReferenceId: staticData.externalReferenceId }),
    ...(formConfig.form.replyToEmail && { replyToEmail: formConfig.form.replyToEmail }),
  }

  // Process each field according to its path
  for (const field of formConfig.form.fields) {
    const value = formData[field.name]

    if (value === undefined || value === null)
      continue

    // Skip empty values for optional fields
    if (!field.required && (value === "" || (Array.isArray(value) && value.length === 0)))
      continue

    if (field.path) {
      const transformedValue = await transformByFieldType(value, field)

      if (field.type === "upload")
        appendToNestedArray(request, field.path, transformedValue)
      else
        setNestedValue(request, field.path, transformedValue, field.name)
    }
  }

  // Merge query header customFields with existing form customFields
  if (customFields && customFields.length > 0) {
    if (!request.other)
      request.other = {}

    // If customFields already exist from form fields, append to them
    if (request.other.customFields && Array.isArray(request.other.customFields))
      request.other.customFields = [...request.other.customFields, ...customFields]
    else
      request.other.customFields = customFields
  }

  return request as ApplicationRequest
}
